/**
 * GroupHeader Sub-component
 *
 * Displays a styled header for a group of items.
 * Optionally sticky to remain visible while scrolling.
 * Supports collapsible mode with a chevron toggle.
 */

import { SelectLabel } from '@/shared/components/ui/select';
import { cn } from '@/shared/utils';
import { getIconComponent, IconKey } from '../icon';
import type { GroupHeaderProps } from './types';

export function GroupHeader({ label, icon, color, isSticky = true, collapsible = false, isExpanded = true, onToggle }: GroupHeaderProps) {
  const IconComponent = icon ? getIconComponent(icon) : undefined;
  const ChevronIcon = getIconComponent(IconKey.ChevronRight);

  return (
    <SelectLabel
      className={cn(
        'flex items-center gap-2 px-2 py-1.5',
        'text-xs font-semibold uppercase tracking-wider text-muted-foreground',
        'bg-muted/50 dark:bg-muted/30',
        isSticky && 'sticky top-0 z-20 backdrop-blur-sm',
        collapsible && 'cursor-pointer select-none hover:bg-muted/80 dark:hover:bg-muted/50'
      )}
      onClick={collapsible ? onToggle : undefined}
    >
      {collapsible && (
        <ChevronIcon
          className={cn(
            'h-3 w-3 shrink-0 transition-transform duration-200',
            isExpanded && 'rotate-90'
          )}
        />
      )}
      {IconComponent && (
        <span
          className="flex h-4 w-4 items-center justify-center rounded"
          style={color ? { backgroundColor: `${color}20` } : undefined}
        >
          <IconComponent
            className="h-3 w-3"
            style={color ? { color } : undefined}
          />
        </span>
      )}
      <span>{label}</span>
    </SelectLabel>
  );
}
